// ResponsibleRegistration — PUBLIC page behind an emailed invite link
// (/responsible-registration/:token). Loads the invite context, shows
// the project + pre-assigned issues, and registers the responsible
// person (company find-or-create with a similar-companies 409 loop).
//
// Data (no auth):
//   GET  /responsible-invites/:token
//   POST /responsible-invites/:token/register
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Card, Input, Button, Tag, Alert, message, Skeleton } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { issueLabel } from "helpers/artifactPrefix";
import {
  getInviteContext,
  registerResponsible,
} from "api/responsibleInvites/responsibleInvitesApi";

const PRIORITY_COLORS = { urgent: "red", normal: "blue", low: "default" };

const fieldStyle = { marginBottom: 12 };
const labelStyle = { fontSize: 13, fontWeight: 600, marginBottom: 4 };

export default function ResponsibleRegistrationPage(props) {
  const params = useParams();
  const token = props.token || params.token;
  const history = useHistory();

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null); // full-page error (expired/used)

  const [companyName, setCompanyName] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [suggestions, setSuggestions] = useState(null); // 409 similar companies
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getInviteContext(token)
      .then((res) => setContext(res))
      .catch((e) => {
        if (e.status === 410) {
          setFatal("This invite link has expired. Ask the project owner to send a new one.");
        } else if (e.status === 409) {
          setFatal("This invite has already been used. If that was you, just log in with your email and password.");
        } else {
          setFatal(e.message || "This invite link is not valid.");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (confirmNewCompany) => {
    setFormError("");
    if (!companyName.trim()) return setFormError("Contractor (company) name is required");
    if (!name.trim()) return setFormError("Your first name is required");
    if (!password || password.length < 8) return setFormError("Password must be at least 8 characters");
    if (password !== confirm) return setFormError("Passwords do not match");

    setSubmitting(true);
    try {
      await registerResponsible(token, {
        companyName: companyName.trim(),
        name: name.trim(),
        lastName: lastName.trim() || undefined,
        password,
        confirmNewCompany: confirmNewCompany || undefined,
      });
      message.success("Account created — you can now log in", 4);
      history.push("/authorization");
    } catch (e) {
      if (e.status === 409 && e.data?.err === "similar_companies_found") {
        setSuggestions(e.data.suggestions || []);
      } else {
        setFormError(e.message || "Registration failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", padding: 16 }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (fatal) {
    return (
      <div style={{ maxWidth: 560, margin: "80px auto", padding: 16, textAlign: "center", fontFamily: "Inter, system-ui, sans-serif" }}>
        <h2>Invite unavailable</h2>
        <p style={{ color: "#666", fontSize: 15 }}>{fatal}</p>
        <Button type="primary" onClick={() => history.push("/authorization")}>
          Go to login
        </Button>
      </div>
    );
  }

  const issues = context?.issues || [];

  return (
    <div style={{ maxWidth: 560, margin: "40px auto", padding: 16, fontFamily: "Inter, system-ui, sans-serif" }}>
      <h2 style={{ marginBottom: 4 }}>
        <UserAddOutlined style={{ marginRight: 8 }} />
        Join {context?.projectTitle || "the project"}
      </h2>
      <p style={{ color: "#666", fontSize: 14 }}>
        You have been invited as a responsible person on{" "}
        <b>{context?.projectTitle || "this project"}</b>. Create your account
        below to see and resolve your assigned issues.
      </p>

      {issues.length > 0 && (
        <Card size="small" title={`Assigned issues (${issues.length})`} style={{ marginBottom: 16 }}>
          {issues.map((i) => (
            <div key={i._id} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 }}>
              {i.displayId ? <b>{`${issueLabel(i)} `}</b> : null}
              {i.title}
              {i.priority && (
                <Tag color={PRIORITY_COLORS[i.priority] || "default"} style={{ marginLeft: 8 }}>
                  {i.priority}
                </Tag>
              )}
              {(i.location?.area || i.location?.level || i.location?.grid) && (
                <span style={{ color: "#999", marginLeft: 6, fontSize: 12 }}>
                  {[i.location?.area, i.location?.level && `L${i.location.level}`, i.location?.grid]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
            </div>
          ))}
        </Card>
      )}

      <Card size="small" title="Create your account">
        <div style={fieldStyle}>
          <div style={labelStyle}>Contractor (company) name</div>
          <Input
            value={companyName}
            placeholder="e.g. Apex Mechanical Inc."
            onChange={(e) => {
              setCompanyName(e.target.value);
              setSuggestions(null);
            }}
          />
        </div>

        {suggestions && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Similar companies already exist"
            description={
              <div>
                <div style={{ marginBottom: 8, fontSize: 13 }}>
                  Pick yours if it is listed — otherwise create a new company.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {suggestions.map((s) => {
                    const sName = typeof s === "string" ? s : s.name || s.title || "";
                    return (
                      <Button
                        key={sName}
                        size="small"
                        onClick={() => {
                          setCompanyName(sName);
                          setSuggestions(null);
                        }}
                      >
                        {sName}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="small"
                  type="link"
                  style={{ paddingLeft: 0, marginTop: 8 }}
                  loading={submitting}
                  onClick={() => submit(true)}
                >
                  None of these — create new company
                </Button>
              </div>
            }
          />
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <div style={labelStyle}>First name</div>
            <Input value={name} placeholder="First name" onChange={(e) => setName(e.target.value)} />
          </div>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <div style={labelStyle}>Last name (optional)</div>
            <Input value={lastName} placeholder="Last name" onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>

        <div style={fieldStyle}>
          <div style={labelStyle}>Email</div>
          <Input value={context?.email || ""} readOnly disabled />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <div style={labelStyle}>Password</div>
            <Input.Password
              value={password}
              placeholder="Min 8 characters"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div style={{ ...fieldStyle, flex: 1 }}>
            <div style={labelStyle}>Confirm password</div>
            <Input.Password
              value={confirm}
              placeholder="Repeat password"
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
        </div>

        {formError && (
          <Alert type="error" showIcon message={formError} style={{ marginBottom: 12 }} />
        )}

        <Button
          type="primary"
          block
          loading={submitting}
          onClick={() => submit(false)}
        >
          Create account
        </Button>
      </Card>
    </div>
  );
}
